import { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareNodes, faSpinner } from '@/fontawesome';
import ShareTradeCard from '@/components/trading/ShareTradeCard';
import { sharePngOrDownload, buildImageFileName } from '@/utils/share-image';
import { showErrorToast } from '@/utils/toasts';
import type { RecommendationSwingtrade, LongTermRecommendation } from '@/models';
import type { TradingRecommendationType } from '@/modelsLocal/trading-recommendation';

interface ShareTradeImageButtonProps {
  trade: RecommendationSwingtrade | LongTermRecommendation;
  type: Extract<TradingRecommendationType, 'SWING' | 'LONGTERM'>;
}

// Matches the width the card was designed at; the capture scales it up from here.
const CARD_WIDTH_PX = 430;

/** Waits for the browser to lay out and paint the freshly mounted card. */
function nextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

/**
 * Renders the trade's result card off-screen, turns it into a PNG and offers it via the
 * native share sheet (or a download as fallback).
 *
 * The card is only mounted while a capture is running, so the extra DOM never costs
 * anything during normal browsing.
 */
export default function ShareTradeImageButton({ trade, type }: ShareTradeImageButtonProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [rendering, setRendering] = useState(false);

  const handleClick = async () => {
    if (rendering) return;

    setRendering(true);
    try {
      // The card mounts with this state change; wait for it to have a real layout
      // before handing the node to the capture.
      await nextPaint();

      const host = hostRef.current;
      if (host === null) throw new Error('Die Karte konnte nicht gerendert werden');

      const fileName = buildImageFileName(
        'bolzai',
        type === 'SWING' ? 'swing' : 'langzeit',
        trade.symbolTicker
      );

      await sharePngOrDownload(host, fileName, `${trade.symbolTicker} - Trade-Ergebnis`);
    } catch (err) {
      console.error('Error creating trade share image:', err);
      showErrorToast('Das Bild konnte nicht erstellt werden. Bitte versuche es erneut.');
    } finally {
      setRendering(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={rendering}
        aria-label="Als Bild teilen"
        title="Als Bild teilen"
        className="flex items-center gap-2 h-8 px-3 rounded-full bg-white/90 dark:bg-gray-800/90 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 shadow-sm cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <FontAwesomeIcon
          icon={rendering ? faSpinner : faShareNodes}
          className={`w-4 h-4 ${rendering ? 'animate-spin' : ''}`}
        />
        <span className="hidden sm:inline text-sm font-medium">Als Bild teilen</span>
      </button>

      {/* Off-screen capture target. Must stay laid out (not display:none, not detached)
          so the node has a real width; position:fixed keeps it out of the page flow. */}
      {rendering && (
        <div
          ref={hostRef}
          aria-hidden="true"
          style={{
            position: 'fixed',
            left: '-20000px',
            top: 0,
            width: `${CARD_WIDTH_PX}px`,
            pointerEvents: 'none'
          }}
        >
          <ShareTradeCard trade={trade} />
        </div>
      )}
    </>
  );
}
